import React from 'react';
import { motion } from 'framer-motion';
import { apps } from '../config/apps';
import { getWindowTheme } from '../config/windowTheme';
import TintedIcon from './TintedIcon';

const APP_HINTS: Record<string, string> = {
  guide:    'Start here',
  about:    'Bio & skills',
  projects: 'GitHub work',
  resume:   'Full CV',
  contact:  'Get in touch',
  artworks: '400+ paintings',
  figma:    'UI/UX designs',
  blog:     'Thoughts & updates',
  notes:    'Leave a guest note',
};

interface StartMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onAppClick: (appId: string) => void;
  isDarkMode?: boolean;
}

const StartMenu: React.FC<StartMenuProps> = ({
  isOpen,
  onClose,
  onAppClick,
  isDarkMode = false,
}) => {
  const t = getWindowTheme(isDarkMode);

  if (!isOpen) return null;

  return (
    <>
      <motion.button
        type="button"
        aria-label="Close start menu"
        className="fixed inset-0 z-[9997] bg-black/30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
      />

      <motion.div
        role="menu"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className={`fixed bottom-[4.5rem] left-3 sm:left-4 z-[9998] w-[min(100vw-1.5rem,20rem)] rounded-lg border overflow-hidden ${t.surface} ${t.border}`}
      >
        <div className={`flex items-center justify-between px-3 py-2.5 border-b ${t.border} ${t.headerBg}`}>
          <div>
            <h2 className={`window-title leading-none ${t.head}`}>Apps</h2>
            <p className={`${t.meta} mt-1`}>{apps.length} applications</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
              isDarkMode
                ? 'bg-white/[0.06] hover:bg-white/10 text-white/50'
                : 'bg-black/[0.05] hover:bg-black/[0.08] text-gray-500'
            }`}
          >
            <span className="text-base leading-none">×</span>
          </button>
        </div>

        <div className="p-1.5 max-h-[min(62vh,24rem)] overflow-y-auto [scrollbar-width:thin]">
          <div className="flex flex-col gap-0.5">
            {apps.map(app => {
              const isImageIcon = typeof app.icon === 'string' && app.icon.includes('.');
              const hint = APP_HINTS[app.id];

              return (
                <button
                  key={app.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onAppClick(app.id);
                    onClose();
                  }}
                  className={`w-full flex items-center gap-2.5 px-2 py-2 rounded text-left transition-colors ${
                    isDarkMode
                      ? 'hover:bg-white/[0.05]'
                      : 'hover:bg-black/[0.03]'
                  }`}
                >
                  <div
                    className={`w-8 h-8 flex-shrink-0 rounded flex items-center justify-center ${
                      isDarkMode ? 'bg-white/[0.04]' : 'bg-black/[0.03]'
                    }`}
                  >
                    {isImageIcon ? (
                      <TintedIcon
                        src={app.icon}
                        isDarkMode={isDarkMode}
                        className="h-6 w-6"
                      />
                    ) : (
                      <span className="text-lg">{app.icon}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className={`block text-xs font-semibold truncate ${t.text}`}>
                      {app.name}
                    </span>
                    {hint && (
                      <span className={`block ${t.meta} truncate`}>{hint}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className={`px-3 py-2 border-t ${t.border} ${t.meta} ${t.headerBg}`}>
          jimmy.mijares · desktop build
        </div>
      </motion.div>
    </>
  );
};

export default StartMenu;
